import { useCallback } from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core';
import ReorderItems from '../reorderItems';
import AdditionalRole from './additionalRole';
import CustomButton from '../customButton';
import { reorder, replaceObjectInList } from '../../../utility/helpers';

const useStyles = makeStyles(() => ({
  root: {
    width: '100%',
  },
  addButton: {
    width: '100%',
  },
}));

const AddAdditionalRolesField = ({
  translations,
  roles = [],
  errors = null,
  onChange,
}) => {
  const classes = useStyles();

  const handleAdd = useCallback(() => {
    onChange([
      ...roles,
      {
        name: '',
        order: roles.length + 1,
      },
    ]);
  }, [roles, onChange]);

  const handleDelete = useCallback(
    index => () => {
      const role = roles[index];
      if (!role?.id) {
        return onChange(roles.filter((_, i) => i !== index));
      }

      return onChange(
        replaceObjectInList(roles, index, { ...role, shouldDelete: true })
      );
    },
    [roles, onChange]
  );

  const handleUndoDelete = useCallback(
    index => () => {
      const role = roles[index];
      if (!role) return;

      onChange(
        replaceObjectInList(roles, index, { ...role, shouldDelete: false })
      );
    },
    [roles, onChange]
  );

  const handleChangeRoleName = useCallback(
    (newName, index) => {
      const role = roles[index];
      if (!role) return;

      onChange(replaceObjectInList(roles, index, { ...role, name: newName }));
    },
    [roles, onChange]
  );

  const handleReorder = useCallback(
    result => {
      if (
        !result.destination ||
        result.source.index === result.destination.index
      )
        return;

      onChange(reorder(roles, result.source.index, result.destination.index));
    },
    [roles, onChange]
  );

  return (
    <div className={classes.root}>
      <ReorderItems onSave={handleReorder}>
        {roles.map((role, index) => (
          <AdditionalRole
            key={`additional_role_${index}`}
            id={index}
            translations={translations}
            role={role}
            hasError={!!errors?.[index]}
            errorMessage={
              translations.validationMessages?.[errors?.[index]?.name]
            }
            onDelete={handleDelete(index)}
            onUndoDelete={handleUndoDelete(index)}
            onChangeName={handleChangeRoleName}
          />
        ))}
      </ReorderItems>
      <CustomButton
        className={classes.addButton}
        type="addWithTextDarkSlimNew"
        onClick={handleAdd}
        fullWidth
      >
        {translations?.addAdditionalRole}
      </CustomButton>
    </div>
  );
};

AddAdditionalRolesField.propTypes = {
  translations: PropTypes.object.isRequired,
  roles: PropTypes.arrayOf(PropTypes.object),
  errors: PropTypes.object,
  onChange: PropTypes.func.isRequired,
};

export default AddAdditionalRolesField;
